import { useMemo } from 'react';

import { TrustSignalsBadgeContainer } from '@dbt-labs/metadata-shared';
import { RyeconClock, RyeconTableRow } from '@dbt-labs/sourdough';
import { RyeconTable } from '@dbt-labs/sourdough';

import { AccessLevel, Maybe } from '@/__generated__/graphql';
import DetailTabs, { TabInfo, TabType } from '@/components/DetailTabs';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { DescriptionChangesProvider } from '@/context/DescriptionChangesProvider';
import { useFeatureFlag } from '@/context/FeatureFlagProvider';
import { ResourceInfoProvider } from '@/context/ResourceInfoProvider';
import { useModel } from '@/hooks/dbtStrategy/useModel';
import { useRecommendationsCount } from '@/hooks/discovery/recommendationsCount';
import { GenericResourceNode } from '@/hooks/discovery/types';
import { useValidatedParams } from '@/hooks/params';
import { useSetMissingCatalog } from '@/hooks/useSetMissingCatalog';
import { useSetMissingTests } from '@/hooks/useSetMissingTests';
import { ErdView } from '@/pages/account/project/erd/ErdView';
import { ParsedUniqueId, parseUniqueId } from '@/typings/parseUniqueId';
import { isDevelopment, UrlEnvironmentType } from '@/typings/url';
import { getDatabaseObjectType } from '@/util/database';
import { getPatchPath } from '@/util/dbt/patchPath';
import {
  formatAbsoluteLocalDate,
  formatDateCommon,
} from '../../../../../../../util/date';
import { useWarehouseHeaderIcon } from '../../hooks/useWarehouseHeaderIcon';
import {
  getResourceTypeHeaderIcon,
  ResourceDetailsHeader,
  TextIconPair,
} from '../Header';
import { ModelNameWithAccessIcon } from '../ModelNameWithAccessIcon';
import {
  ResourceStatus,
  ResourceStatusResult,
} from '../Sections/ResourceStatusSection';
import { CodeView } from '../Tabs/CodeView';
import { ColumnsView } from '../Tabs/ColumnsView';
import { EvaluatorView } from '../Tabs/EvaluatorView';
import { GeneralView } from '../Tabs/GeneralView';
import { PerformanceView } from '../Tabs/PerformanceView';

const useTabs = (
  environmentType: UrlEnvironmentType,
  violationsCount: number,
  enableRecommendations: boolean,
  enablePerformance: boolean,
  enableRelationships: boolean,
) => {
  return useMemo(() => {
    const tabs: TabInfo[] = [
      { type: 'general' },
      { type: 'code' },
      { type: 'columns' },
      ...(enableRelationships ? [{ type: 'relationships' as TabType }] : []),
      ...(isDevelopment(environmentType) || !enablePerformance
        ? []
        : [{ type: 'performance' as TabType }]),
      ...(enableRecommendations
        ? [{ type: 'evaluator' as TabType, count: violationsCount ?? undefined }]
        : []),
    ];
    return tabs;
  }, [
    enablePerformance,
    enableRecommendations,
    enableRelationships,
    environmentType,
    violationsCount,
  ]);
};

export const modelStatusCriteriaFn = (runStatus: ResourceStatus) => {
  switch (runStatus) {
    case 'error':
      return ResourceStatusResult.error;
    case 'success':
      return ResourceStatusResult.pass;
    case 'skipped':
      return ResourceStatusResult.warn;
    default:
      return ResourceStatusResult.unknown;
  }
};

export type ResourceParams = {
  uniqueId: ParsedUniqueId;
};

export const ModelView = ({ uniqueId }: ResourceParams) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const { environmentId } = useDbtCloudProject();
  const { environmentType, projectId } = useValidatedParams();
  const isErdEnabled = useFeatureFlag('meta6937ErdView', false);

  const modelQuery = useModel(parseUniqueId(uniqueId.raw));
  const model = modelQuery.fetching ? null : modelQuery.data;

  const warehouseHeaderIcon = useWarehouseHeaderIcon({
    uniqueId,
    environmentType,
  });

  const [recommendationCount] = useRecommendationsCount({
    environmentId,
    uniqueId: uniqueId.raw,
  });
  const tabs = useTabs(
    environmentType,
    recommendationCount.data?.recommendations?.rules?.totalCount || 0,
    hasAccountFeature('explorerRecommendations'),
    hasAccountFeature('explorerModelPerformance'),
    isErdEnabled,
  );

  const appliedData = model?.applied;

  useSetMissingTests(appliedData);
  useSetMissingCatalog(appliedData);

  if (modelQuery.error) return <>{modelQuery.error.stack}</>;
  const date = model?.applied?.executionInfo?.executeCompletedAt
    ? new Date(model.applied.executionInfo.executeCompletedAt)
    : undefined;
  const updatedAt = date && formatAbsoluteLocalDate(date);
  const updatedAtUtc = date && formatDateCommon(date).utc;
  const objectType =
    model?.applied?.catalog?.type && getDatabaseObjectType(model.applied?.catalog.type);
  const rowCount = model?.applied?.catalog?.rowCountStat ?? undefined;
  const headerIcons: TextIconPair[] = [getResourceTypeHeaderIcon('model')];

  if (updatedAt) {
    headerIcons.push({
      ryecon: RyeconClock,
      text: `Last run ${updatedAt}`,
      tooltip: updatedAtUtc || undefined,
    });
  }
  if (objectType) {
    headerIcons.push({
      ryecon: RyeconTable,
      text: `${objectType}`,
    });
  }
  if (warehouseHeaderIcon) {
    headerIcons.push(warehouseHeaderIcon);
  }
  if (rowCount) {
    headerIcons.push({
      ryecon: RyeconTableRow,
      text: `${rowCount.toLocaleString()} rows`,
    });
  }

  const latestGitSha = model?.latestGitSha;

  return (
    <>
      <ResourceDetailsHeader
        uniqueId={uniqueId.raw}
        headerIcons={headerIcons}
        title={uniqueId.remainder}
        packageName={uniqueId.packageName}
        files={[model?.applied?.patchPath, model?.applied?.filePath]}
        resourceType="model"
        name={model?.applied?.name ?? ''}
        versions={model?.applied?.versions}
        updatedDate={date}
        deprecationDate={model?.applied?.deprecationDate || null}
        rightAdornment={
          <TrustSignalsBadgeContainer
            resourceType="model"
            trustSignals={{ healthIssues: model?.applied?.healthIssues }}
            size="lg"
            className="mt-1"
          />
        }
      >
        {!hasAccountFeature('explorerAccountUI') && (
          <ModelNameWithAccessIcon
            accessLevel={(model?.applied?.access as Maybe<AccessLevel>) ?? null}
            uniqueId={uniqueId}
            versions={model?.applied?.versions}
          />
        )}
      </ResourceDetailsHeader>
      <DetailTabs tabs={tabs} show={!!model}>
        {(activeTab) => {
          return (
            <ResourceInfoProvider
              resourceType="model"
              resourceIdentifiers={model?.applied?.name ? [model?.applied?.name] : []}
              filePath={getPatchPath(model?.applied)}
              latestGitSha={latestGitSha}
            >
              {activeTab === 'general' && (
                <DescriptionChangesProvider entityType="model" projectId={projectId}>
                  <GeneralView
                    resource={model?.applied as GenericResourceNode}
                    uniqueId={uniqueId}
                    description={model?.applied?.description}
                    isDescriptionInherited={model?.applied?.isDescriptionInherited}
                    meta={model?.applied?.meta}
                  />
                </DescriptionChangesProvider>
              )}
              {activeTab === 'code' && (
                <CodeView
                  rawCode={model?.applied?.rawCode}
                  compiledCode={model?.applied?.compiledCode}
                />
              )}
              {activeTab === 'columns' && model?.applied && (
                <ColumnsView resource={model.applied} />
              )}
              {activeTab === 'relationships' && (
                <ErdView modelUniqueId={uniqueId.raw} />
              )}
              {activeTab === 'performance' && (
                <PerformanceView
                  uniqueId={uniqueId.raw}
                  executionInfo={model?.applied?.executionInfo}
                />
              )}
              {activeTab === 'evaluator' && <EvaluatorView uniqueId={uniqueId.raw} />}
            </ResourceInfoProvider>
          );
        }}
      </DetailTabs>
    </>
  );
};
